import React, { FC, useEffect, useState } from 'react';
import { useFlowTools } from 'flowTools/useFlowTools';
import {
  useItineraryActions,
  getSelectedItinerary,
} from 'clientState/providers/ShoppingBundleProvider/ActionsProvider';
import {
  useAlternativeItineraryLayerActions,
  useIsAlternativeItineraryLayerOpen,
} from 'clientState/providers/AlternativeItineraryProvider/AlternativeItineraryLayerProvider';
import { useAlternativeItineraryActions } from 'clientState/providers/AlternativeItineraryProvider/AlternativeItineraryCalculatorProvider';
import AlternativeItineraryDoubleCardLayerMobile from './AlternativeItineraryDoubleCardLayerMobile';
import AvailabilityModal from '@components/Results/Components/Modals/AvailabilityModal';
import { useFeeType } from 'clientState/providers/FeeTypeProvider';
import { useItinerarySelection } from 'clientState/providers/ItinerarySelectionProvider';
import { useShoppingCartActions } from 'clientState/providers/ShoppingBundleProvider/ShoppingCartRequestHooks';
import {
  AlternativeItineraryDoubleCardLayerBaseProps,
  AlternativeItineraryDoubleCardLayerProps,
} from './types';
import {
  usePersistedSessionState,
  useHistory,
} from '@edreamsodigeo/frontend-core';
import { TRIP_SUMMARY_PAGE } from 'clientState/providers/NavigationProvider';
import AlternativeItineraryDoubleCardLayerDesktop from '@components/AlternativeItineraryDoubleCard/components/AlternativeItineraryDoubleCardLayer/AlternativeItineraryDoubleCardLayerDesktop';
import { getSearchUrl } from '@components/AlternativeFlight/helpers';
import { PageType } from 'clientState/providers/AlternativeItineraryProvider/hooks';

const AlternativeItineraryDoubleCardLayerBaseDefault: FC<
  AlternativeItineraryDoubleCardLayerBaseProps
> = ({
  isOpen,
  onClose,
  itinerary,
  selectedItinerary,
  discountAmount,
  valueProposition,
  valuePropositions,
  additionalInformation,
  isBrokenFlow,
  nonIssuable = true,
  isUnavailable = false,
  hasCloseIcon = true,
  showContinueButton,
  loading,
  showLoadingBar,
  isShoppingCartLoading = false,
  onContinue,
  onChange,
  overrideAction,
  trackingHandler,
  simpleUI,
  pageName = PageType.FlightsResults,
}) => {
  const { isMobile } = useFlowTools();
  return isMobile ? (
    <AlternativeItineraryDoubleCardLayerMobile
      isOpen={isOpen}
      onClose={onClose}
      itinerary={itinerary}
      selectedItinerary={selectedItinerary}
      discountAmount={discountAmount}
      isBrokenFlow={isBrokenFlow}
      nonIssuable={nonIssuable}
      isUnavailable={isUnavailable}
      hasCloseIcon={hasCloseIcon}
      showContinueButton={showContinueButton}
      valueProposition={valueProposition}
      valuePropositions={valuePropositions}
      additionalInformation={additionalInformation}
      loading={loading}
      showLoadingBar={showLoadingBar}
      isShoppingCartLoading={isShoppingCartLoading}
      onContinue={onContinue}
      onChange={onChange}
      overrideAction={overrideAction}
      trackingHandler={trackingHandler}
      simpleUI={simpleUI}
      pageName={pageName}
    />
  ) : null;
};

export const AlternativeItineraryDoubleCardLayerBaseVersioned: FC<
  AlternativeItineraryDoubleCardLayerBaseProps
> = ({
  isOpen,
  itinerary,
  selectedItinerary,
  discountAmount,
  valueProposition,
  valuePropositions,
  additionalInformation,
  isBrokenFlow,
  nonIssuable = true,
  loading,
  isShoppingCartLoading = false,
  onContinue,
  onChange,
  onClose,
  trackingHandler,
}) => {
  const { isDesktop } = useFlowTools();
  return isDesktop ? (
    <AlternativeItineraryDoubleCardLayerDesktop
      isOpen={isOpen}
      loading={loading}
      isShoppingCartLoading={isShoppingCartLoading}
      itinerary={itinerary}
      valueProposition={valueProposition}
      valuePropositions={valuePropositions}
      discountAmount={discountAmount}
      selectedItinerary={selectedItinerary}
      isBrokenFlow={isBrokenFlow}
      nonIssuable={nonIssuable}
      additionalInformation={additionalInformation}
      onContinue={onContinue}
      onChange={onChange}
      onClose={onClose}
      trackingHandler={trackingHandler}
    />
  ) : null;
};

const AlternativeItineraryDoubleCardLayer: FC<
  AlternativeItineraryDoubleCardLayerProps
> = ({
  itinerary,
  selectedItinerary,
  discountAmount,
  valueProposition,
  valuePropositions,
  additionalInformation,
  alternativeItineraryPosition,
  nonIssuable,
  isUnavailable,
  hasCloseIcon = true,
  isShoppingCartLoading,
  loading,
  errors,
  trackingHandler,
  simpleUI,
  pageName,
}) => {
  const [shouldNavigate, setShouldNavigate] = useState(false);
  const [loadSend, setLoadSend] = useState(false);
  const { navigate } = useHistory() as any;
  const { showItineraryTrip } = useItineraryActions();
  const setItineraryInfo = useItinerarySelection();
  const { setContinueWithErrorBookeableState } =
    useAlternativeItineraryActions();
  const { isOpen, isErrorLayerVisible } = useIsAlternativeItineraryLayerOpen();
  const { close } = useAlternativeItineraryLayerActions();
  const { feeTypeSelected } = useIsAlternativeItineraryLayerOpen();
  const [feeType] = useFeeType();
  const feeTypeId = feeTypeSelected || feeType?.id;

  const handleButtonAction = (action: string) => {
    if (action === 'close') {
      setLoadSend(false);
      close();
      return;
    }

    const itineraryToContinue =
      action === 'continue' ? selectedItinerary : itinerary;

    if (action === 'change') {
      trackingHandler('select', 'alternative', 'button');
    }
    if (action === 'continue') {
      trackingHandler('discard', 'button', 'CR');
    }

    setItineraryInfo({
      itinerary: getSelectedItinerary(itineraryToContinue, feeTypeId),
    });
    setShouldNavigate(true);
    close();
  };
  const handleContinue = () => handleButtonAction('continue');
  const handleChange = () => handleButtonAction('change');

  const handleClose = () => handleButtonAction('close');

  useEffect(() => {
    if (isOpen && !loadSend && alternativeItineraryPosition != null) {
      setLoadSend(true);
      trackingHandler('onLoad', '');
    }
  }, [
    isOpen,
    alternativeItineraryPosition,
    loadSend,
    setLoadSend,
    trackingHandler,
  ]);

  useEffect(() => {
    if (shouldNavigate) {
      navigate(TRIP_SUMMARY_PAGE, { push: true });
      showItineraryTrip();
      setShouldNavigate(false);
    }
  }, [shouldNavigate, navigate, showItineraryTrip]);

  if (!nonIssuable && errors) {
    setContinueWithErrorBookeableState(true);
    return null;
  }

  return !errors && !isErrorLayerVisible ? (
    <AlternativeItineraryDoubleCardLayerBaseDefault
      isOpen={isOpen}
      onClose={handleClose}
      itinerary={itinerary}
      selectedItinerary={selectedItinerary}
      discountAmount={discountAmount}
      valueProposition={valueProposition}
      valuePropositions={valuePropositions}
      additionalInformation={additionalInformation}
      nonIssuable={nonIssuable}
      isUnavailable={isUnavailable}
      hasCloseIcon={hasCloseIcon}
      loading={loading}
      isShoppingCartLoading={isShoppingCartLoading}
      onContinue={handleContinue}
      onChange={handleChange}
      trackingHandler={trackingHandler}
      simpleUI={simpleUI}
      pageName={pageName}
    />
  ) : (
    <AvailabilityModal open={isOpen} onClose={close} onClick={close} />
  );
};

export const AlternativeItineraryDoubleCardLayerDiscountDesktop: FC<
  AlternativeItineraryDoubleCardLayerProps
> = ({
  itinerary,
  selectedItinerary,
  discountAmount,
  valueProposition,
  valuePropositions,
  additionalInformation,
  alternativeItineraryPosition,
  nonIssuable,
  goToResults,
  loading,
  errors,
  trackingHandler,
  simpleUI,
  pageName,
}) => {
  const [loadSend, setLoadSend] = useState(false);
  const [searchParameters] = usePersistedSessionState('searchParameters');
  const { isOpen, isErrorLayerVisible } = useIsAlternativeItineraryLayerOpen();
  const { selectItineraryAndContinue } = useShoppingCartActions();
  const { close } = useAlternativeItineraryLayerActions();
  const [feeType] = useFeeType();
  const { feeTypeSelected } = useIsAlternativeItineraryLayerOpen();
  const feeTypeId = feeTypeSelected || feeType?.id;

  const handleButtonAction = (action: string) => {
    if (action === 'close') {
      trackingHandler('discard', 'backdrop', 'OP');
      setLoadSend(false);
      close();
      return;
    }

    if (action === 'continue') {
      trackingHandler('discard', 'button', 'CR');
      if (nonIssuable && goToResults) {
        const searchUrl = getSearchUrl(searchParameters);
        window.location.assign(searchUrl);
      } else if (nonIssuable) {
        close();
        return;
      }
    }

    if (action === 'change') {
      trackingHandler('select', 'alternative', 'button');
    }

    const itineraryToContinue =
      action === 'continue' ? selectedItinerary : itinerary;

    selectItineraryAndContinue(
      getSelectedItinerary(itineraryToContinue, feeTypeId),
      { feeTypeId: feeTypeId }
    );
    close();
  };
  const handleContinue = () => handleButtonAction('continue');
  const handleChange = () => handleButtonAction('change');

  useEffect(() => {
    if (isOpen && !loadSend && alternativeItineraryPosition != null) {
      setLoadSend(true);
      trackingHandler('onLoad', '');
    }
  }, [
    isOpen,
    alternativeItineraryPosition,
    loadSend,
    setLoadSend,
    trackingHandler,
  ]);

  if (!nonIssuable && errors) {
    selectItineraryAndContinue(
      getSelectedItinerary(selectedItinerary, feeTypeId),
      { feeTypeId: feeTypeId }
    );
    return null;
  }

  return !errors && !isErrorLayerVisible ? (
    <AlternativeItineraryDoubleCardLayerBaseVersioned
      isOpen={isOpen}
      onClose={() => handleButtonAction('close')}
      itinerary={itinerary}
      selectedItinerary={selectedItinerary}
      discountAmount={discountAmount}
      valueProposition={valueProposition}
      valuePropositions={valuePropositions}
      additionalInformation={additionalInformation}
      nonIssuable={nonIssuable}
      loading={loading}
      onContinue={handleContinue}
      onChange={handleChange}
      trackingHandler={trackingHandler}
      simpleUI={simpleUI}
      pageName={pageName}
    />
  ) : (
    <AvailabilityModal open={isOpen} onClose={close} onClick={close} />
  );
};

export const AlternativeItineraryDoubleCardLayerBase: FC<
  AlternativeItineraryDoubleCardLayerBaseProps
> = (props) => {
  const { isMobile } = useFlowTools();
  const Component = isMobile
    ? AlternativeItineraryDoubleCardLayerBaseDefault
    : AlternativeItineraryDoubleCardLayerBaseVersioned;

  return <Component {...props} />;
};

export default AlternativeItineraryDoubleCardLayer;
